/* global rwgps */
import React, {Fragment} from 'react'
import styles from './FindHills.scss'
import {findAscents, findDescents} from 'lib/insights/hill_finder'
import {FullBleedList} from '../FullBleedList'
import {Distance, Elevation, Grade} from 'common/units'
import _ from 'lodash'
import {rawPoints} from 'lib/propTypes'

export class FindHills extends React.PureComponent {
  static propTypes = {
    'rawPoints': rawPoints
  }

  constructor (props) {
    super(props)
    this.state = {
      climbs: null,
      descents: null,
      climbsExpanded: false,
      descentsExpanded: false
    }
  }

  componentDidMount () {
    this.setState(this.calculate())
  }

  componentDidUpdate (prevProps) {
    if (this.props.rawPoints !== prevProps.rawPoints) {
      this.setState(this.calculate())
    }
  }

  calculate = () => {
    const {rawPoints} = this.props

    if (!rawPoints || rawPoints.length === 0) return {}

    const trackPoints = rwgps.TrackPoint.fromJSON(rawPoints).trackPoints

    return {
      climbs: findAscents(trackPoints),
      descents: findDescents(trackPoints)
    }
  }

  toggleDescents = () => {
    this.setState((prevState) => ({descentsExpanded: !prevState.descentsExpanded}))
  }

  toggleClimbs = () => {
    this.setState((prevState) => ({climbsExpanded: !prevState.climbsExpanded}))
  }

  render () {
    const {rawPoints, onSegmentSelected, onSegmentHovered} = this.props
    const {climbs, climbsExpanded, descents, descentsExpanded} = this.state

    if (!rawPoints || (!climbs && !descents)) return null

    const descentsToShow = descentsExpanded ? descents : descents.slice(0, 3)
    const climbsToShow = climbsExpanded ? climbs : climbs.slice(0, 3)

    return (
      <div className={styles.FindHills}>
        {climbs && climbs.length > 0 && <div className={styles.segmentsBlock}>
          <h2>Climbs</h2>
          <FullBleedList
            header={<Fragment><div>Distance</div><div>Elevation</div><div>Grade</div></Fragment>}>
            {climbsToShow.map((climb, i) =>
              <ElevationSegment
                key={i}
                rawPoints={rawPoints}
                startI={climb.first_i}
                stopI={climb.last_i}
                climb
                onMouseOver={() => onSegmentHovered([climb.first_i, climb.last_i])}
                onMouseOut={() => onSegmentHovered(null)}
                onClick={() => onSegmentSelected([climb.first_i, climb.last_i], true)} />
            )}
          </FullBleedList>
          {climbs.length > 3 && <div className={styles.expandlink}>
            <a onClick={this.toggleClimbs}>{climbsExpanded ? 'Collapse Climbs' : 'Show All Climbs'}</a>
          </div>}
        </div>}
        {descents && descents.length > 0 && <div className={styles.segmentsBlock}>
          <h2>Descents</h2>
          <FullBleedList
            header={<Fragment><div>Distance</div><div>Elevation</div><div>Grade</div></Fragment>}>
            {descentsToShow.map((descent, i) =>
              <ElevationSegment
                key={i}
                rawPoints={rawPoints}
                startI={descent.first_i}
                stopI={descent.last_i}
                descent
                onMouseOver={() => onSegmentHovered([descent.first_i, descent.last_i])}
                onMouseOut={() => onSegmentHovered(null)}
                onClick={() => onSegmentSelected([descent.first_i, descent.last_i], true)} />
            )}
          </FullBleedList>
          {descents.length > 3 && <div className={styles.expandlink}>
            <a onClick={this.toggleDescents}>{descentsExpanded ? 'Collapse Descents' : 'Show All Descents'}</a>
          </div>}
        </div>}
      </div>
    )
  }
}

class ElevationSegment extends React.PureComponent {
  /*
   * Most of this code was duplicated from SegmentPopup.js.  If any needs to change with the way metrics
   * are calculated, or throttled, etc. chance are they also need to be updated over there as well.
   */

  constructor (props) {
    super(props)
    this._throttledCalculateMetrics = _.throttle(this.calculateMetrics, 500)
    this.state = {
      metrics: null
    }
  }

  componentDidMount () {
    this.calculateMetrics()
  }

  componentDidUpdate (prevProps) {
    if (
      (prevProps.startI !== this.props.startI) ||
      (prevProps.stopI !== this.props.stopI)
    ) {
      this._throttledCalculateMetrics()
    }
  }

  initData () {
    const {rawPoints} = this.props
    const tpsResult = window.rwgps.TrackPoint.fromJSON(rawPoints)
    const points = tpsResult.trackPoints
    const data = new window.rwgps.Data(points)
    this._data = data
  }

  calculateMetrics = () => {
    const {startI, stopI} = this.props

    if (!this._data) this.initData()

    // old RSP includes the last point in the highlight slice when it does metrics calculation
    const dataSlice = this._data.slice(startI, stopI - startI + 1)
    const mc = new window.rwgps.MetricsCalculator(dataSlice, false)
    const metrics = mc.calculate()
    this.setState({metrics})
  }

  render () {
    const {climb, descent, onMouseOver, onMouseOut, onClick} = this.props

    const {metrics} = this.state
    if (!metrics) { return null } // wait until we have metrics for first render to prevent content jumping

    return (
      <li
        onMouseOver={() => onMouseOver()}
        onMouseOut={() => onMouseOut()}
        onClick={() => onClick()}>
        <div><Distance value={metrics.distance} bigUnits short /></div>
        {climb && <div><Elevation value={metrics.ele_gain} short /></div>}
        {descent && <div><Elevation value={metrics.ele_loss} short /></div>}
        <div>{metrics.grade && <Grade value={metrics.grade.avg || 0} short />}</div>
      </li>
    )
  }
}
